/**
 * MED-938 / E3.5 — "Completed Sessions" list (most recent first). Each row
 * shows the date block, session label, duration + attendance, and the E3.4
 * feedback status badge ("Feedback due" vs "Complete"). Omitted entirely when
 * there are no completed sessions.
 *
 * MED-1063: the "Feedback due" nudge is attendance-aware — an absent
 * participant is not told feedback is due (feedback isn't available to them).
 * A retained pre-absence submission (AC5) still shows "Complete".
 *
 * MED-1198 (D12+D17): each completed row gets a persistent "Post-Session"
 * button (right side) that re-opens the session's post-session page — the
 * durable return path the MED-1145 Needs Attention "Open Recap" chip only gave
 * to the single most-recent led session. The button branches on `is_leading`:
 * a session the viewer led routes to the cohort-scoped peer-leader page
 * (recap composer, still re-sendable), everything else to the session-scoped
 * participant page (feedback/notes/summary). It renders only for LSD
 * group-coaching sessions — the only sessions with a post-session page — so
 * non-LSD rows (1:1, workshops) that share this modality-agnostic list stay
 * button-less (AC6). It adds an entry point only; neither page's state,
 * editability, or locks change (AC5/AC7).
 */
import { Badge, Box, Button, Card, Divider, Group, Stack, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import { Check, Clock } from 'react-feather';
import type { DashboardRefreshCompletedSession } from '../../../services/api/participantPortal.js';
import { DateBlock } from './DateBlock.js';
import { formatDuration, sessionLabel } from './dashboardRefreshUtils.js';

/**
 * MED-1198 (AC3/AC4/AC6) — post-session route for a completed row, or null when
 * the row has no post-session page. Only LSD group-coaching sessions have one;
 * the leader page is cohort-scoped, the participant page session-scoped.
 */
function postSessionPath(session: DashboardRefreshCompletedSession): string | null {
  if (session.modality_type !== 'group_coaching') return null;
  return session.is_leading
    ? `/participant/cohorts/${session.cohort_id}/sessions/${session.id}/post-session`
    : `/participant/sessions/${session.id}/post-session`;
}

/**
 * MED-1240 — attendance label for the meta sub-line, derived from the resolved
 * `attendance_status`. The prior logic was `attended ? 'Attended' : 'Missed'`,
 * which collapsed every non-attended state — including `pending`/no-row (never
 * recorded) and `partial` — into "Missed". Now:
 *   - `attended` (and legacy `present`/`late`) → "Attended"           (AC3)
 *   - `partial`                                → "Partially Attended" (AC4)
 *   - `absent` (explicit mark only)            → "Missed"             (AC1)
 *   - `pending` / no row / `excused` / unknown → "Attendance Pending" (AC2, italic)
 * `pending: true` signals the un-recorded, non-final state so the caller can
 * italicise just those words. There is no coach-led/peer-led branch — the
 * mapping is identical for every participant (AC5).
 */
function attendanceLabel(status: string | null): { label: string; pending: boolean } {
  switch (status) {
    case 'attended':
    case 'present':
    case 'late':
      return { label: 'Attended', pending: false };
    case 'partial':
      return { label: 'Partially Attended', pending: false };
    case 'absent':
      return { label: 'Missed', pending: false };
    default:
      // 'pending', null (no attendance row), 'excused' (dead), or any unknown
      // value — all un-recorded / non-final.
      return { label: 'Attendance Pending', pending: true };
  }
}

/**
 * Meta sub-line: "{duration} · {attendance}". The duration keeps the existing
 * dimmed/small style; only the "Attendance Pending" words are italicised (AC2)
 * via the inheriting inner span, which preserves the size and dimmed colour.
 *
 * MED-1251: when the viewer led this session (`is_leading`), a "· Led" suffix
 * is appended after the attendance label so a leader can tell at a glance which
 * completed sessions they ran versus merely attended. The suffix is driven by
 * `is_leading` alone — independent of the attendance value — so it composes
 * with any state, appending after "Attended", "Partially Attended", or the
 * italic "Attendance Pending" alike (AC3). It sits outside the italicised span
 * so it stays upright even when the attendance words are italic, and inherits
 * the dimmed/xs style of the outer Text. `is_leading` is only ever true for
 * peer-led group-coaching sessions, so no explicit modality branch is needed
 * (AC4). The role-agnostic `attendanceLabel()` mapping is intentionally left
 * unchanged (MED-1240) — the led signal is a separate suffix, not a new label.
 */
function MetaLine({ session }: { session: DashboardRefreshCompletedSession }) {
  const duration = formatDuration(session.duration_minutes);
  const { label, pending } = attendanceLabel(session.attendance_status);
  return (
    <Text size="xs" c="dimmed" truncate>
      {duration ? `${duration} · ` : ''}
      <Text span inherit fs={pending ? 'italic' : undefined}>
        {label}
      </Text>
      {session.is_leading ? ' · Led' : ''}
    </Text>
  );
}

function CompletedRow({ session }: { session: DashboardRefreshCompletedSession }) {
  // MED-1198: durable re-open control (null for non-LSD rows — AC6).
  const reopenPath = postSessionPath(session);

  return (
    <Group justify="space-between" wrap="nowrap" gap="md" py="xs">
      <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        <DateBlock iso={session.scheduled_start} tone="green" />
        <Box style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>
            {sessionLabel(session)}
          </Text>
          <MetaLine session={session} />
        </Box>
      </Group>

      <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
        {session.feedback_complete ? (
          <Badge
            size="sm"
            variant="light"
            color="medleyGreen"
            leftSection={<Check size={11} />}
            style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
          >
            Complete
          </Badge>
        ) : session.is_absent ? (
          // MED-1063: feedback isn't due for an absent participant — no nudge.
          // "Missed" in the meta line already conveys non-attendance.
          null
        ) : (
          <Badge
            size="sm"
            variant="light"
            color="warningAmber"
            style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
          >
            Feedback due
          </Badge>
        )}

        {/* MED-1198 (AC1/AC2): persistent re-open control on every LSD
            group-coaching completed row, routed by is_leading (AC3/AC4). */}
        {reopenPath && (
          <Button
            component={Link}
            to={reopenPath}
            size="xs"
            variant="light"
            color="medleyGreen"
          >
            Post-Session
          </Button>
        )}
      </Group>
    </Group>
  );
}

export function CompletedSessionsSection({
  sessions,
}: {
  sessions: DashboardRefreshCompletedSession[];
}) {
  if (sessions.length === 0) return null;

  return (
    <Card withBorder radius="md" padding="md" mb="lg">
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Clock size={16} />
          <Text fw={600} size="sm">
            Completed Sessions
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
        </Text>
      </Group>

      <Stack gap={0}>
        {sessions.map((session, i) => (
          <Box key={session.id}>
            {i > 0 && <Divider my="xs" />}
            <CompletedRow session={session} />
          </Box>
        ))}
      </Stack>
    </Card>
  );
}
